import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import i18n from '~/components/i18n';
import { RouteTypes } from '~/routes/constants';
import { useDebounce } from 'use-lodash-debounce';
import { SurveyQuery } from '~/QueryHooks/Survey';
import notify from '~/components/Notify';
import { Input, Popup, Tab } from 'semantic-ui-react';
import { El } from '~/components/Layout/style';
import { useNavigate } from 'react-router-dom';
import TableComponent from '~/components/TableComponent';
import Dialog from '~/components/Dialog';
import SurveyService from '~/api/services/survey';
import dayjs from 'dayjs';
import { DownloadService } from '~/api/services/downloadFiles';
import { LIGHT_USER_ROLE_SLUG, SURVEY_STATUS_KEYS } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import CustomIcon from '~/components/CustomIcon';
import { handleErrors } from '~/utils/helpers';

interface ISurvey { }

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Survey title'),
    key: 'title'
  },
  {
    name: i18n.t('Participants'),
    key: 'participants',
    type: 'survey-participants'
  },
  {
    name: i18n.t('Questions'),
    key: 'questionsCount'
  },
  {
    name: i18n.t('Start / End'),
    key: 'start',
    type: 'startEndSurvey',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.SURVEY_SHOW
  },
  {
    popupMessage: i18n.t('Edit'),
    type: 'link',
    name: 'pencil',
    size: 'large',
    link: RouteTypes.SURVEY_EDIT,
    display: [SURVEY_STATUS_KEYS.defined],
    disableForLightUser: true
  },
  {
    popupMessage: i18n.t('Plan'),
    type: 'icon',
    name: 'check circle outline',
    size: 'large',
    actionType: 'plan',
    display: [SURVEY_STATUS_KEYS.defined],
    renderActions: () => <></>,
    disableForLightUser: true
  },
  {
    popupMessage: i18n.t('Survey results'),
    type: 'link',
    name: 'info circle',
    size: 'large',
    link: RouteTypes.SURVEY_SHOW_RESUTLS
  },
  {
    popupMessage: i18n.t('Export'),
    type: 'icon',
    name: 'file excel',
    size: 'large',
    actionType: 'export',
    display: [SURVEY_STATUS_KEYS.completed]
  },
  {
    popupMessage: i18n.t('Recreate'),
    type: 'link',
    name: 'refresh',
    size: 'large',
    link: RouteTypes.SURVEY_RECREATE,
    disableForLightUser: true
  },
  {
    popupMessage: i18n.t('Preview'),
    type: 'link',
    name: 'file pdf',
    size: 'large',
    link: RouteTypes.SURVEY_QUESTIONS_PREVIEW
  },
  {
    popupMessage: i18n.t('Statistics'),
    type: 'link',
    name: 'pie chart',
    size: 'large',
    link: RouteTypes.SURVEY_STATISTICS,
    display: [SURVEY_STATUS_KEYS.completed, SURVEY_STATUS_KEYS.live]
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    display: [SURVEY_STATUS_KEYS.defined],
    disableForLightUser: true
  }
]

const Survey: React.FunctionComponent<ISurvey> = () => {
  const { t } = useTranslation();
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    status: 'actual',
    column: '',
    sort: ''
  })
  const actions = getActions()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const navigate = useNavigate();
  const [deleteSurveyData, setDeleteSurveyData] = useState(null)
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)
  const [isBusy, setIsBusy] = useState(false)
  const [planData, setPlanData] = useState(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const {
    isLoading: isBusyData,
    data: surveyData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = SurveyQuery.getSurveys({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: surveyCount,
    isLoading: isBusySurveyCount,
    refetch: fetchCount
  } = SurveyQuery.getSurveysCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: surveyOptions,
    refetch: refetchOptions,
    isLoading: isBusyOptions
  } = SurveyQuery.getSurveysOptions()

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
      fetchCount()
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      size='large'
      onClick={() => navigate(RouteTypes.SURVEY_CREATE)}
    >
      {t('Create')}
    </El.PageHeaderActionButton>
  }

  async function handleDownloadExport(id) {
    try {
      await DownloadService.downloadFile(`surveys/${id}/export`, t('survey_export'))
    } catch (error) {
      handleErrors(error)
    }
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'delete':
        return setDeleteSurveyData(row)
      case 'export':
        return handleDownloadExport(row.id)
      default:
        return
    }
  }

  function renderPlanAction(row) {
    const displayAction = row?.status === SURVEY_STATUS_KEYS.defined && row.questionsCount > 0
    return displayAction && <Popup
      content={t('Plan')}
      trigger={<CustomIcon
        onClick={() => setPlanData(row)}
        style={{ cursor: 'pointer' }}
        name='check circle outline'
        size='large'
      />}
    />
  }

  function renderContent() {
    const actionsFiltered = lightUserLogged ? actions.filter(action => !action.disableForLightUser) : actions
    const planActionIndex = actionsFiltered.findIndex(action => action.actionType === 'plan')
    if (planActionIndex >= 0) {
      //@ts-ignore
      actionsFiltered[planActionIndex].renderActions = renderPlanAction
    }
    return <TableComponent
      actions={actionsFiltered}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={handleClickAction}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = surveyOptions.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      });
      setActiveTabIndex(activeIndex)
    }
  }

  function renderPanes() {
    const panes = surveyOptions.map((option) => {
      const countIndex = surveyCount.findIndex((count) => count.status == option.key);
      return {
        menuItem: `${option.label}  (${surveyCount[countIndex]?.count || 0})`,
        render: () => renderContent()
      };
    });

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  async function handleDeleteSurvey() {
    setIsBusy(true);
    try {
      await SurveyService.deleteSurvey(deleteSurveyData?.id)
      notify(t('Successfully deleted survey'), 'success')
      fetchFilteredData();
      fetchCount()
      setDeleteSurveyData(null)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderDeleteModal() {
    return <Dialog
      isDialogOpen={Boolean(deleteSurveyData)}
      handleClose={() => setDeleteSurveyData(null)}
      textHeader={t('Delete survey')}
      textContent={t('Do you really want to delete survey {{title}}?', { title: deleteSurveyData?.title })}
      textSubmit={t('Delete')}
      textClose={t('Close')}
      type={'abort'}
      handleSubmit={() => handleDeleteSurvey()}
    />
  }

  async function handlePlan() {
    setIsBusy(true);
    try {
      await SurveyService.planSurvey(planData?.id)
      notify(t('Successfully planed survey'), 'success')
      setPlanData(null)
      fetchFilteredData();
      fetchCount()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function getTextContent() {
    const start = dayjs(planData?.start).format(('dddd, D MMMM YYYY HH:mm'))
    const end = dayjs(planData?.end).format(('dddd, D MMMM YYYY HH:mm'))
    return t('The survey {{id}} with start date {{start}} and end date {{end}} hereby planned.', { id: planData?.uniqueId, start, end })
  }

  function renderPlanModal() {
    return <Dialog
      isDialogOpen={Boolean(planData)}
      disableSubmit={isBusy}
      handleClose={() => setPlanData(null)}
      textHeader={t('Plan survey {{id}}', { id: planData?.uniqueId })}
      textContent={getTextContent()}
      textSubmit={t('Plan')}
      textClose={t('Cancel process')}
      type={'abort'}
      handleSubmit={() => handlePlan()}
    />
  }

  const { filtered, rows } = surveyData ? surveyData : { filtered: 0, rows: [] };
  const loading = isBusyOptions || isBusyData || isBusySurveyCount || isBusy

  return <Layout
    segmented
    isBusy={loading}
    title={t('Survey')}
    filters={renderSearch()}
    actions={!lightUserLogged && renderActions()}
  >
    {planData && renderPlanModal()}
    {deleteSurveyData && renderDeleteModal()}
    {surveyData && surveyCount && surveyOptions && renderPanes()}
  </Layout>;
};

export default Survey;
